import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import api from '../api/axios';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #23272f;
  color: #fff;
  padding-top: var(--tg-content-safe-area-inset-top, 0px);
  padding-bottom: var(--tg-content-safe-area-inset-bottom, 0px);
`;

const Logo = styled.div`
  font-size: 32px;
  font-weight: bold;
  padding: 20px;
  text-align: center;
  color: #4b69ff;
`;

const SwiperContainer = styled.div`
  flex: 1;
  width: 100%;
  padding: 20px;
`;

const Slide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  height: 100%;
`;

const SlideImage = styled.img`
  width: 100%;
  max-width: 300px;
  height: auto;
  margin-bottom: 24px;
  border-radius: 16px;
`;

const SlideTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
  text-align: center;
`;

const SlideDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #ccc;
  text-align: center;
  margin-bottom: 24px;
`;

const BottomSection = styled.div`
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-top: 2px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #ccc;
  line-height: 1.4;
`;

const StartButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: ${props => props.disabled ? 'rgba(75, 105, 255, 0.3)' : '#4b69ff'};
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #3a56d4;
  }
`;

const OnboardingView = ({ setAgreed }) => {
	const [isAgreed, setIsAgreed] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [checking, setChecking] = useState(true);
	const navigate = useNavigate();
	const { t } = useTranslation();

	useEffect(() => {
		const checkAgreements = async () => {
			try {
				const res = await api.get('/user/check-agreements');
				if (res.success && res.agreed) {
					navigate('/');
				} else {
					setChecking(false);
				}
			} catch (e) {
				setChecking(false);
			}
		};
		checkAgreements();
	}, [navigate]);

	const slides = [
		{
			image: '/assets/images/onboarding/slide1.png',
			title: t('onboarding.slide1.title'),
			description: t('onboarding.slide1.description')
		},
		{
			image: '/assets/images/onboarding/slide2.png',
			title: t('onboarding.slide2.title'),
			description: t('onboarding.slide2.description')
		},
		{
			image: '/assets/images/onboarding/slide3.png',
			title: t('onboarding.slide3.title'),
			description: t('onboarding.slide3.description')
		}
	];

	const handleStart = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await api.post('/user/agreements');
			if (response.success) {
				localStorage.setItem('termsAccepted', 'true');
				if (setAgreed) setAgreed(true);
				navigate('/');
			} else {
				setError('Ошибка подтверждения. Попробуйте ещё раз.');
			}
		} catch (e) {
			setError('Ошибка подтверждения. Попробуйте ещё раз.');
		} finally {
			setLoading(false);
		}
	};

	if (checking) {
		return <Container><div style={{ textAlign: 'center', marginTop: 64 }}>{t('common.loading') || 'Загрузка...'}</div></Container>;
	}

	return (
		<Container>
			<Logo>SkinForge</Logo>

			<SwiperContainer>
				<Swiper
					modules={[Pagination, Autoplay]}
					pagination={{ clickable: true }}
					spaceBetween={20}
					slidesPerView={1}
					autoplay={{ delay: 4000, disableOnInteraction: false }}
				>
					{slides.map((slide, index) => (
						<SwiperSlide key={index}>
							<Slide>
								<SlideImage src={slide.image} alt={slide.title} />
								<SlideTitle>{slide.title}</SlideTitle>
								<SlideDescription>{slide.description}</SlideDescription>
							</Slide>
						</SwiperSlide>
					))}
				</Swiper>
			</SwiperContainer>

			<BottomSection>
				<CheckboxContainer>
					<Checkbox
						type="checkbox"
						id="terms"
						checked={isAgreed}
						onChange={(e) => setIsAgreed(e.target.checked)}
					/>
					<CheckboxLabel htmlFor="terms">
						{t('onboarding.terms.prefix')}{' '}
						<a href="https://www.notion.so/TERMS-OF-SERVICE-20403b226f588097b37fead691134a65" target="_blank" rel="noopener noreferrer" style={{ color: '#4b69ff' }}>
							{t('onboarding.terms.agreement')}
						</a>{' '}
						{t('onboarding.terms.and')}{' '}
						<a href="https://www.notion.so/PRIVACY-POLICY-20403b226f58805896ffe221c5a77ff6" target="_blank" rel="noopener noreferrer" style={{ color: '#4b69ff' }}>
							{t('onboarding.terms.privacy')}
						</a>
					</CheckboxLabel>
				</CheckboxContainer>

				{error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
				<StartButton
					onClick={handleStart}
					disabled={!isAgreed || loading}
				>
					{loading ? t('common.loading') : t('onboarding.start')}
				</StartButton>
			</BottomSection>
		</Container>
	);
};

export default OnboardingView; 